import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Copy, MapPin, Trophy } from "lucide-react";
import { toast } from "sonner";
import logoMark from "@/assets/smg-logo-mark.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://smgsportsmgt.com";

const LEVEL_STYLES = {
  Professional: "bg-volt text-ink",
  "Emerging Pro": "bg-[#10B981] text-ink",
  Amateur: "bg-[#FACC15] text-ink",
  "Youth / NIL": "bg-[#2563EB] text-white",
};

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ink text-slate-50">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-3 sm:px-12">
          <Link to="/" data-testid="athlete-profile-logo-link">
            <span className="relative inline-block">
              <img src={logoMark} alt="SMG Sports Management" className="h-10 w-auto" />
              <span
                className="absolute -right-3 -top-1 font-mono text-[9px] text-slate-400"
                data-testid="athlete-profile-logo-tm"
              >
                ™
              </span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="athlete-profile-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>
      <main className="relative mx-auto w-full max-w-6xl px-6 py-14 sm:px-12">{children}</main>
      <footer className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-6 py-8 text-[11px] text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-12">
          <p>A Division of SMG Media Group · Carlsbad, CA</p>
          <p>
            © 2026 SMG Media Group ·{" "}
            <a href="mailto:brian@smgmediagroup.com?cc=jacob@smgmediagroup.com" data-testid="athlete-profile-email-link" className="transition-colors hover:text-volt">
              brian@smgmediagroup.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default function AthleteProfilePage() {
  const { id } = useParams();
  const [athlete, setAthlete] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/roster/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => mounted && setAthlete(d))
      .catch(() => mounted && setAthlete(undefined));
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (athlete && athlete.name) {
      document.title = `${athlete.name} — SMG Sports Management`;
      const desc = `${athlete.name} — ${athlete.level} pickleball athlete on the SMG Sports Management roster. ${((athlete.bio || "")).slice(0, 160)}`;
      const setMeta = (attr, key, content) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      setMeta("name", "description", desc);
      setMeta("property", "og:title", `${athlete.name} — SMG Sports Management`);
      setMeta("property", "og:description", desc);
      setMeta("property", "og:type", "profile");
      setMeta("property", "og:url", `${SITE}/athlete/${id}`);
      setMeta("name", "twitter:card", "summary");
      setMeta("name", "twitter:title", `${athlete.name} — SMG Sports Management`);
      setMeta("name", "twitter:description", desc);
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", `${SITE}/athlete/${id}`);
      if (athlete.photo_path) {
        setMeta("property", "og:image", `${API}/files/${athlete.photo_path}`);
        setMeta("name", "twitter:image", `${API}/files/${athlete.photo_path}`);
      }
    }
    return () => {
      document.title = "SMG Sports Management Group International — Pickleball Representation";
    };
  }, [athlete, id]);

  useEffect(() => {
    if (athlete && athlete.name) {
      const t = setTimeout(() => {
        document
          .querySelector("[data-testid=athlete-profile-partner-cta]")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 800);
      return () => clearTimeout(t);
    }
  }, [athlete]);

  if (athlete === null) {
    return (
      <Shell>
        <p className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-slate-500" data-testid="athlete-profile-loading">
          Loading profile…
        </p>
      </Shell>
    );
  }

  if (athlete === undefined) {
    return (
      <Shell>
        <div className="py-24 text-center" data-testid="athlete-profile-not-found">
          <h1 className="font-display text-4xl uppercase text-white">Athlete not found</h1>
          <p className="mx-auto mt-4 max-w-md text-sm text-slate-400">
            This profile may have been removed or is not yet published.
          </p>
          <Link
            to="/"
            data-testid="athlete-profile-notfound-back-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-volt px-8 py-3.5 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
          >
            Back to site
          </Link>
        </div>
      </Shell>
    );
  }

  const highlights = (athlete.highlights || "")
    .split(";")
    .map((h) => h.trim())
    .filter(Boolean);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Profile link copied to clipboard.");
    } catch {
      toast.error("Could not copy the link — copy it from your browser's address bar.");
    }
  };

  return (
    <Shell>
      <div className="grid items-start gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="relative" data-testid="athlete-profile-photo-frame">
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-volt/10 blur-3xl" aria-hidden="true" />
            <div className="overflow-hidden rounded-3xl border border-white/10">
              {athlete.photo_path ? (
                <img
                  src={`${API}/files/${athlete.photo_path}`}
                  alt={athlete.name}
                  className="w-full object-cover"
                  data-testid="athlete-profile-photo"
                />
              ) : (
                <div className="flex aspect-[4/5] items-center justify-center bg-gradient-to-br from-elev to-ink">
                  <span className="font-display text-8xl text-volt/60">
                    {athlete.name.slice(0, 1).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <span
              className={`absolute left-5 top-5 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${
                LEVEL_STYLES[athlete.level] || "bg-volt text-ink"
              }`}
              data-testid="athlete-profile-level-chip"
            >
              {athlete.level}
            </span>
          </div>
        </div>

        <div className="lg:col-span-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
            SMG Roster · Athlete Profile
          </p>
          <h1 className="mt-3 font-display text-5xl uppercase leading-[1.02] text-white sm:text-6xl" data-testid="athlete-profile-name">
            {athlete.name}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-xs uppercase tracking-wider text-slate-400">
            {athlete.ranking && (
              <span className="inline-flex items-start gap-2 whitespace-pre-line text-volt" data-testid="athlete-profile-ranking">
                <Trophy className="mt-0.5 h-4 w-4 shrink-0" /> {athlete.ranking}
              </span>
            )}
            {athlete.location && (
              <span className="inline-flex items-center gap-2" data-testid="athlete-profile-location">
                <MapPin className="h-4 w-4" /> {athlete.location}
              </span>
            )}
          </div>

          {athlete.bio && (
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="athlete-profile-bio">
              {athlete.bio}
            </p>
          )}

          {highlights.length > 0 && (
            <div className="mt-8 max-w-2xl rounded-2xl border border-white/10 bg-surface p-6" data-testid="athlete-profile-highlights">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                Career Highlights
              </p>
              <ul className="mt-4 space-y-2.5">
                {highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-slate-200">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="/#partners"
              data-testid="athlete-profile-partner-cta"
              className="partner-pulse inline-flex items-center gap-2 rounded-full bg-volt px-8 py-4 font-display text-sm uppercase tracking-wider text-ink transition-all hover:bg-volt-light"
            >
              Partner with an Athlete
            </a>
            {athlete.media_kit && (
              <a
                href={athlete.media_kit.startsWith("http") ? athlete.media_kit : `${API}/files/${athlete.media_kit}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.gtag?.("event", "file_download", { file_name: "media_kit", athlete: athlete.name })}
                data-testid="athlete-profile-media-kit-btn"
                className="inline-flex items-center gap-2 rounded-full border border-volt/50 px-6 py-4 font-display text-sm uppercase tracking-wider text-volt transition-all hover:bg-volt hover:text-ink"
              >
                Download Media Kit (PDF)
              </a>
            )}
            <button
              onClick={copyLink}
              data-testid="athlete-profile-copy-link-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
            >
              <Copy className="h-4 w-4" /> Copy Profile Link
            </button>
          </div>
        </div>
      </div>
    </Shell>
  );
}
